import {
  ICompanyDigitalCertificates,
  ICompanyDigitalCertificateTypeDocument,
} from "../../../models/company-digital-certificate";
import { CNPJMask, CPFMask } from "../../../utils/maks";

export const COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND = {
  CPF: "CPF",
  CNPJ: "CNPJ",
} as const;

export type TCompanyDigitalCertificateDocumentKind =
  (typeof COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND)[keyof typeof COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND];

export const COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_DIGITS = {
  CPF: 11,
  CNPJ: 14,
} as const;

export const COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_MAX_LENGTH = {
  CPF: CPFMask.mask.length,
  CNPJ: CNPJMask.mask.length,
} as const;

type TDigitalCertificateDocumentTypeSource = {
  typeDocument?: ICompanyDigitalCertificateTypeDocument | null;
  typeDocumentCode?: string | null;
  typeDocumentName?: string | null;
};

const DOCUMENT_KIND_BY_CODE: Record<string, TCompanyDigitalCertificateDocumentKind> =
  {
    "1": COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CPF,
    "2": COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CNPJ,
    CPF: COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CPF,
    CNPJ: COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CNPJ,
  };

const normalizeDocumentTypeValue = (value?: string | null) =>
  value?.trim().toUpperCase() ?? "";

export const getCompanyDigitalCertificateDocumentDigits = (
  value?: string | null,
) => value?.replace(/\D/g, "") ?? "";

export const getCompanyDigitalCertificateDocumentKind = ({
  typeDocument,
  typeDocumentCode,
  typeDocumentName,
}: TDigitalCertificateDocumentTypeSource): TCompanyDigitalCertificateDocumentKind | null => {
  const possibleValues = [
    typeDocument?.code,
    typeDocumentCode,
    typeDocument?.value,
    typeDocumentName,
    typeDocument?.name,
    typeDocument?.description,
  ];

  for (const value of possibleValues) {
    const normalizedValue = normalizeDocumentTypeValue(value);

    if (!normalizedValue) continue;

    if (normalizedValue.includes(COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CNPJ))
      return COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CNPJ;

    if (normalizedValue.includes(COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CPF))
      return COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CPF;

    if (DOCUMENT_KIND_BY_CODE[normalizedValue]) {
      return DOCUMENT_KIND_BY_CODE[normalizedValue];
    }
  }

  return null;
};

export const getCompanyDigitalCertificateDocumentMask = (
  kind: TCompanyDigitalCertificateDocumentKind | null,
) => {
  if (kind === COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CPF) {
    return CPFMask.mask;
  }

  if (kind === COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CNPJ) {
    return CNPJMask.mask;
  }

  return undefined;
};

export const getCompanyDigitalCertificateDocumentMaxLength = (
  kind: TCompanyDigitalCertificateDocumentKind | null,
) => {
  if (kind === COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CPF) {
    return COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_MAX_LENGTH.CPF;
  }

  if (kind === COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CNPJ) {
    return COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_MAX_LENGTH.CNPJ;
  }

  return COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_MAX_LENGTH.CNPJ;
};

export const formatCompanyDigitalCertificateDocument = ({
  document,
  typeDocument,
  typeDocumentCode,
  typeDocumentName,
}: Pick<
  ICompanyDigitalCertificates,
  "document" | "typeDocument" | "typeDocumentCode" | "typeDocumentName"
>) => {
  const digits = getCompanyDigitalCertificateDocumentDigits(document);

  if (!digits) return "";

  const kind = getCompanyDigitalCertificateDocumentKind({
    typeDocument,
    typeDocumentCode,
    typeDocumentName,
  });

  if (kind === COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CPF) {
    return CPFMask.resolve(digits);
  }

  if (kind === COMPANY_DIGITAL_CERTIFICATE_DOCUMENT_KIND.CNPJ) {
    return CNPJMask.resolve(digits);
  }

  return document;
};
